import { Text, TextField, Tabs, Box, Container, Flex, Heading, Card, Code, Badge, Button, Dialog, Separator } from "@radix-ui/themes";
import { MagnifyingGlassIcon, ArrowLeftIcon, CheckIcon } from "@radix-ui/react-icons" // Added ArrowLeftIcon
import { useState, useEffect, useMemo } from 'react';

// Define the structure of an entity from entities.json
interface Entity {
  id: number;
  name: string;
  type: 'TEAMS' | 'COMPETITIONS' | 'CHANNELS' | 'CHANNEL_GROUPS';
}

interface Preset {
  preset_name: string;
  entities: { id: number; type: Entity['type'] }[];
}

// Helper function to resolve entity details from allEntities
const resolveEntityDetails = (allEntities: Entity[], partialEntity: { id: number; type: Entity['type'] }): Entity => {
  const fullEntity = allEntities.find(
    (e) => e.id === partialEntity.id && e.type === partialEntity.type
  );
  return fullEntity || { id: partialEntity.id, name: `Unknown ${partialEntity.type} (ID: ${partialEntity.id})`, type: partialEntity.type };
};


const ExploreContent = () => {
  return (
    <Flex direction="column" gap="4" px="4" py="9">
      <TrendingSection />
    </Flex>
  )
}

export default ExploreContent;

const TrendingSection = () => {
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [allPresets, setAllPresets] = useState<Preset[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entitiesPerPage = 10;

  const [selectedTrendingTopics, setSelectedTrendingTopics] = useState<Entity[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [entityToReplace, setEntityToReplace] = useState<Entity | null>(null);
  const [activePresetName, setActivePresetName] = useState<string>(''); // No preset selected initially
  const [isCustomPresetActive, setIsCustomPresetActive] = useState<boolean>(false);
  const [currentFirebaseJson, setCurrentFirebaseJson] = useState<Entity[]>([]); // Stubbed Firebase state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);

  // Effect to load entities.json
  useEffect(() => {
    fetch('/data/entities.json')
      .then(response => response.json())
      .then((data: Entity[]) => setAllEntities(data))
      .catch(error => console.error("Error loading entities.json:", error));
  }, []); // Runs once on mount

  // Effect to load presets.json
  useEffect(() => {
    fetch('/data/presets.json')
      .then(response => response.json())
      .then((data: Preset[]) => setAllPresets(data))
      .catch(error => console.error("Error loading presets.json:", error));
  }, []); // Runs once on mount

  // Effect to load true trending topics from Firebase endpoint
  useEffect(() => {
    if (allEntities.length > 0) {
      fetch('/config/trending_topics')
        .then(response => response.json())
        .then((data: { id: number; type: Entity['type'] }[]) => {
          const resolvedEntities = data.map(partialEntity =>
            resolveEntityDetails(allEntities, partialEntity)
          );
          setCurrentFirebaseJson(resolvedEntities);
          setSelectedTrendingTopics(resolvedEntities);
        })
        .catch(error => console.error("Error loading trending topics from Firebase:", error));
    }
  }, [allEntities]);

  const filteredEntities = useMemo(() => {
    return allEntities.filter(entity =>
      entity.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allEntities, searchTerm]);

  const totalPages = Math.ceil(filteredEntities.length / entitiesPerPage);
  const indexOfLastEntity = currentPage * entitiesPerPage;
  const indexOfFirstEntity = indexOfLastEntity - entitiesPerPage;
  const currentEntities = filteredEntities.slice(indexOfFirstEntity, indexOfLastEntity);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getPaginationGroup = () => {
    const maxPageLinks = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageLinks / 2));
    let endPage = Math.min(totalPages, startPage + maxPageLinks - 1);

    if (endPage - startPage + 1 < maxPageLinks) {
      startPage = Math.max(1, endPage - maxPageLinks + 1);
    }

    const pages: (number | string)[] = [];
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const onTrendingItemClick = (entity: Entity) => {
    setEntityToReplace(entity);
    setIsDialogOpen(true);
  };

  const handleReplace = (replacementIndex: number) => {
    if (entityToReplace) {
      const updatedTopics = [...selectedTrendingTopics];
      updatedTopics[replacementIndex] = entityToReplace;
      setSelectedTrendingTopics(updatedTopics);
      setIsDialogOpen(false);
      setEntityToReplace(null);
      setIsCustomPresetActive(true); // Custom preset is active after manual replacement
      setActivePresetName('Custom');
    }
  };

  const onPresetSelect = (preset: Preset) => {
    const resolvedEntities = preset.entities.map(partialEntity =>
      resolveEntityDetails(allEntities, partialEntity)
    );
    setSelectedTrendingTopics(resolvedEntities);
    setActivePresetName(preset.preset_name);
    setIsCustomPresetActive(false);
  };

  const isDrafting = useMemo(() => {
    // Compare selectedTrendingTopics with currentFirebaseJson
    // Simple comparison for now (length and IDs), can be more robust if order/details matter
    if (selectedTrendingTopics.length !== currentFirebaseJson.length) return true;
    const currentIds = currentFirebaseJson.map(e => `${e.id}-${e.type}`).sort().join(',');
    const selectedIds = selectedTrendingTopics.map(e => `${e.id}-${e.type}`).sort().join(',');
    return currentIds !== selectedIds;
  }, [selectedTrendingTopics, currentFirebaseJson]);

  useEffect(() => {
    if (publishSuccess && isDrafting) {
      setPublishSuccess(false);
    }
  }, [isDrafting, publishSuccess]);

  const handleUndoChanges = () => {
    setSelectedTrendingTopics(currentFirebaseJson);
    setActivePresetName(''); // No preset selected when reverting to current
    setIsCustomPresetActive(false);
    setPublishSuccess(false);
  };


  const currentJsonOutput = JSON.stringify(selectedTrendingTopics.map(topic => ({ id: topic.id, type: topic.type })), null, 2);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await fetch('/config/trending_topics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: currentJsonOutput,
      });

      if (!response.ok) {
        throw new Error('Failed to publish');
      }

      setCurrentFirebaseJson(selectedTrendingTopics);
      setPublishSuccess(true);
    } catch (error) {
      console.error(error);
      alert('Error publishing to Firebase. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };


  return (
    <Box>
      <Heading size="8" mb="4">Trending</Heading> {/* Added mb="4" */}
      <Flex direction="row" gap="3"> {/* Added gap="3" */}

        {/* 2/3 WIDTH: CURRENT SELECTIONS + PRESETS */}
        <Box style={{ flexGrow: 1, flexShrink: 0, flexBasis: '50%' }}>
          <Box>
            {/* Inner Flex for the Trending Items */}
            <Flex direction="row" gap="3">
              {selectedTrendingTopics.map((topic, index) => (
                <TrendingItem
                  key={topic.id}
                  id={topic.id}
                  name={topic.name}
                  entityType={topic.type}
                />
              ))}
            </Flex>
          </Box>

          <Heading size="3">Presets</Heading>
          <Flex direction="column" gap="2" mb="4"> {/* Added mb="4" */}
            {allPresets.map((preset) => (
              <PresetItem
                key={preset.preset_name}
                preset={preset}
                isActive={activePresetName === preset.preset_name && !isCustomPresetActive}
                onClick={onPresetSelect}
              />
            ))}
            <PresetItem
              preset={{ preset_name: "Custom", entities: [] }}
              isActive={isCustomPresetActive}
              onClick={() => { setActivePresetName('Custom'); setIsCustomPresetActive(true); }} // Explicitly activate custom
            />
          </Flex>
        </Box>

        {/* 1/3 WIDTH: JSON OUTPUT */}
        <Box style={{ flexGrow: 1, flexShrink: 0, flexBasis: '50%', border: '1px solid var(--gray-a4)' }} p="3"> {/* Added p="3" */}
          <Flex direction="column" gap="3"> {/* Changed to column to stack elements */}
            <Flex direction="row" justify="between" align="center"> {/* Flex for header line */}
              <Heading size="5" mb="0">JSON Output</Heading> {/* Heading moved out of the dynamic button group */}
              <Flex direction="row" gap="1" align="center"> {/* Group for Undo Arrow, Badge, Publish */}
                {publishSuccess ? (
                  <Button size="1" variant="solid" color="grass" style={{ cursor: 'default' }}>
                    <CheckIcon /> Published
                  </Button>
                ) : (
                  <>
                    {isDrafting && (
                      <Button variant="ghost" size="1" onClick={handleUndoChanges} style={{ cursor: 'pointer' }}>
                        <ArrowLeftIcon />
                      </Button>
                    )}
                    <Badge color={isDrafting ? 'orange' : 'grass'} mb="0">{isDrafting ? 'Drafting' : 'Current'}</Badge>
                    {isDrafting && ( // Publish button visible only when drafting
                      <Button size="1" variant="solid" color="grass" onClick={handlePublish} disabled={isPublishing}>
                        {isPublishing ? 'Publishing...' : 'Publish'}
                      </Button>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
            <Code size="2" color="gray" variant="ghost" style={{ whiteSpace: 'pre' }}>
              {currentJsonOutput}
            </Code>
          </Flex>
        </Box>

      </Flex>

      {/* Replaced Custom Configuration Heading with Separator */}
      <Separator size="4" my="4" />

      <TextField.Root placeholder="Search every team, competition and channel" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}>
        <TextField.Slot>
          <MagnifyingGlassIcon height="16" width="16" />
        </TextField.Slot>
      </TextField.Root>

      {/* List of entities */}
      <Flex direction="column" gap="2" mt="4">
        {currentEntities.map(entity => (
          <TrendingItem
            key={entity.id}
            id={entity.id}
            name={entity.name}
            entityType={entity.type}
            onClick={() => onTrendingItemClick(entity)} // Add onClick handler
          />
        ))}
      </Flex>

      {/* Pagination controls */}
      <Flex justify="center" gap="2" mt="4">
        {totalPages > 1 && (
          <>
            {currentPage > 1 && (
              <Button variant="soft" onClick={() => paginate(currentPage - 1)}>
                Previous
              </Button>
            )}
            {getPaginationGroup().map((item, index) => (
              <Button
                key={index}
                variant={currentPage === item ? 'solid' : 'soft'}
                onClick={() => typeof item === 'number' && paginate(item)}
                disabled={typeof item === 'string'}
              >
                {item}
              </Button>
            ))}
            {currentPage < totalPages && (
              <Button variant="soft" onClick={() => paginate(currentPage + 1)}>
                Next
              </Button>
            )}
          </>
        )}
      </Flex>

      <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Replace Trending Topic</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Which trending topic would you like to replace with "{entityToReplace?.name}"?
          </Dialog.Description>

          <Flex direction="column" gap="3">
            {selectedTrendingTopics.map((topic, index) => (
              <Button key={topic.id} onClick={() => handleReplace(index)}>
                Replace "{topic.name}"
              </Button>
            ))}
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

    </Box>
  )
}

const PresetItem = ({ preset, isActive, onClick }: PresetProps) => {
  return (
    <Box width="100%">
      <Card
        onClick={() => onClick(preset)}
        variant={isActive ? 'surface' : 'classic'} // 'surface' for active, 'classic' for inactive
        style={{
          cursor: 'pointer',
          borderColor: isActive ? 'var(--accent-9)' : 'var(--gray-a4)',
          borderStyle: 'solid',
          borderWidth: '1px',
        }}
      >
        <Flex gap="3" align="center">
          <Box>
            <Text as="div" size="2" weight="bold">
              {preset.preset_name}
            </Text>
          </Box>
        </Flex>
      </Card>
    </Box>
  );
};

const TrendingItem = ({ id, name, entityType, onClick }: TrendingItemsProps) => {
  const badgeColor = entityColorMap[entityType];
  return (
    <Box minWidth="180px" maxWidth="240px">
      <Card onClick={onClick ? () => onClick({ id, name, type: entityType }) : undefined}>
        <Flex gap="3" align="center">
          <Box>
            <Text as="div" size="2" weight="bold">
              {name}
            </Text>
            <Badge color={badgeColor}>{entityType}</Badge>
          </Box>
        </Flex>
      </Card>
    </Box>)
}

interface PresetProps {
  preset: Preset;
  isActive: boolean;
  onClick: (preset: Preset) => void;
}

interface TrendingItemsProps {
  id: number;
  name: string;
  entityType: 'TEAMS' | 'COMPETITIONS' | 'CHANNELS' | 'CHANNEL_GROUPS';
  onClick?: (entity: Entity) => void;
}

const entityColorMap = {
  TEAMS: 'blue',
  COMPETITIONS: 'red',
  CHANNELS: 'indigo',
  CHANNEL_GROUPS: 'green',
} as const;


